import { SagaGenerator, call, takeLeading, put, takeEvery, select } from "typed-redux-saga";
import { AnyAction } from "redux";

import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { ActivityActions } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { UaaActions, UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { clearOperationLock, setOperationLoading, setOperationLock } from "../../../app/slices/operation/reducers";
import { DOC_LOCK_OPERATION } from "../../../app/slices/operation/types";
import { TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { getUserInfo } from "../../../utilities/user";
import { isValidLockDocumentOperationResponse } from "../../../utilities/rpcHelper";
import {
	callLockDocumentOperation,
	DOCUMENT_LOCK_GROUP,
	shouldUnLockProcess,
	showLockErrorNotification
} from "../../../utilities/lockProcessUtils";
import { selectOperationLockState, selectOperationLockStateByGroup } from "../../../app/slices/operation/selectors";

import {
	lockProcess,
	lockProcessError,
	renewLockProcess,
	stopLockProcessPolling,
	unlockProcess
} from "../store/actions";

import { requestEditableAction } from "./actions";

export function* lockProcessSaga(): SagaGenerator<void> {
	yield* takeLeading(
		(action: AnyAction) => lockProcess.match(action) || renewLockProcess.match(action),
		function* handleLock(action) {
			if (action.payload.docRef) {
				yield* call(handleLockProcess, action.payload.docRef);
			}
		}
	);

	yield* takeLeading(unlockProcess, function* handleLock(action) {
		if (action.payload.docRef) {
			yield* call(handleUnlockProcess, action.payload.docRef);
		}
	});

	yield* takeLeading(lockProcessError, function* handleUnlock() {
		yield* call(handleLockProcessError);
	});

	yield* takeEvery(
		(action: AnyAction) =>
			ActivityActions.cancel.match(action) && action.payload.activityId === TASK_DETAIL_ACTIVITY_ID,
		function* () {
			yield* call(handleUnlockProcess, undefined);
		}
	);

	yield* takeEvery([UaaActions.loggingOut], function* () {
		yield* call(handleUnlockProcess, undefined);
	});

	yield* takeLeading(ApplicationActions.startMainActivityRequested, function* handleStartMainActivity() {
		yield* put(setOperationLoading({ operationName: DOC_LOCK_OPERATION, loadingState: "idle" }));
	});
}

export function* handleLockProcess(docRef: string): SagaGenerator<void> {
	const user = yield* select(UaaSelectors.user);
	const userInfo = getUserInfo(user);

	if (!userInfo) {
		return;
	}

	const response = yield* callLockDocumentOperation(docRef, TASK_DETAIL_ACTIVITY_ID, "LOCK", userInfo);

	if (!isValidLockDocumentOperationResponse(response)) {
		return;
	}
	yield* put(
		setOperationLock({
			group: DOCUMENT_LOCK_GROUP,
			reference: docRef,
			userId: response.processLockedByUserId,
			userFullName: response.processLockedByUserFullName,
			lockedAt: response.processLockedAt,
			lockExpiration: response.processLockExpiration
		})
	);
	yield* put(requestEditableAction({}));
}

export function* handleUnlockProcess(docRef: string | undefined): SagaGenerator<void> {
	const lockState = docRef
		? yield* select(selectOperationLockState(docRef))
		: (yield* select(selectOperationLockStateByGroup(DOCUMENT_LOCK_GROUP)))[0];
	yield* put(stopLockProcessPolling({}));
	yield* put(clearOperationLock({ group: DOCUMENT_LOCK_GROUP }));
	const user = yield* select(UaaSelectors.user);
	const userInfo = getUserInfo(user);

	if (lockState && shouldUnLockProcess(lockState, user) && userInfo) {
		yield* call(callLockDocumentOperation, lockState.reference, TASK_DETAIL_ACTIVITY_ID, "UNLOCK", userInfo);
	}
}

export function* handleLockProcessError(): SagaGenerator<void> {
	yield* showLockErrorNotification(TASK_DETAIL_ACTIVITY_ID);
	yield* put(ApplicationActions.setBusy(false));
}
